import React, { createContext, useState } from "react";
import {
    addLineItem,
    applyDiscountCode,
    getCheckout,
    getOrCreateCheckoutId,
    removeDiscountCode,
    updateLineItem,
    applyProxyDiscountV2,
} from "services/storefront/cart";
import axios from "axios";
import getStableCheckout from "helpers/checkout-build-function";
import { checkoutProxyURL, checkoutKey } from "utils/constants";
import checkWarrantyProducts from "@root/helpers/checkWarrantyProducts";
import navigate from "@helpers/link-navigate";
import sendLog from "@root/utils/new-relic-logger";
import NEW_RELIC_LEVELS from "@root/constants/NEW_RELIC_LEVELS";

const defaultPrice = { currencyCode: "USD", amount: 0 };

// Set on the Shopify "thank you" page after an order is completed so the
// headless storefront knows to discard the now-completed cart and start fresh.
const CART_COMPLETED_COOKIE = "sealskin_cart_completed";

const getCookie = (name) => {
    if (typeof document === "undefined") return null;
    const match = document.cookie
        .split(";")
        .map((cookie) => cookie.trim())
        .find((cookie) => cookie.startsWith(`${name}=`));
    return match ? decodeURIComponent(match.split("=").slice(1).join("=")) : null;
};

// Best-effort delete: the cookie may have been set on the apex domain (e.g.
// ".sealskincovers.com") so it's shared with the Shopify checkout, so we expire
// it against the current host and its parent domain as well.
const deleteCookie = (name) => {
    if (typeof document === "undefined") return;
    const expired = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    document.cookie = expired;

    const { hostname } = window.location;
    const parts = hostname.split(".");
    for (let i = 0; i < parts.length - 1; i += 1) {
        const domain = parts.slice(i).join(".");
        document.cookie = `${expired}; domain=.${domain}`;
        document.cookie = `${expired}; domain=${domain}`;
    }
};

const initialState = {
    items: [],
    error: "",
    checkoutUrl: "",
    discountCode: "",
    totalPrice: defaultPrice,
    subtotalPrice: defaultPrice,
    discountPrice: defaultPrice,
    createdAt: "",
    updatedAt: "",
    hasDiscount: "",
    isLoading: true,
};

// Validates a Storefront cart mutation response and returns the cart on
// success. Throws a `CartMutationError` with the full response attached when
// the cart is missing, so callers' existing try/catch blocks get a clean,
// descriptive error instead of a `Cannot read properties of undefined` /
// `undefined is not an object` TypeError. The New Relic logger already reads
// `error.response.data`, so the failure context flows into NewRelic for free.
const extractCartFromMutation = (response, mutationKey) => {
    const mutationResult = response?.data?.[mutationKey];
    const cart = mutationResult?.cart;
    const userErrors = mutationResult?.userErrors || [];

    if (!cart) {
        const graphqlErrors = response?.errors || [];
        const reason = userErrors.length > 0
            ? userErrors.map((ue) => ue?.message || ue?.code).filter(Boolean).join("; ")
            : (graphqlErrors.map((ge) => ge?.message).filter(Boolean).join("; ") || `Empty ${mutationKey} response`);

        const error = new Error(`Shopify ${mutationKey} returned no cart: ${reason}`);
        error.name = "CartMutationError";
        error.response = {
            data: {
                mutationKey,
                graphqlErrors,
                userErrors,
                mutationResult: mutationResult ?? null,
            },
        };
        throw error;
    }

    return { cart, userErrors };
};

const getAppliedDiscountCodeErrors = (discountCode) => {
    switch (discountCode) {
        case "DISCOUNT_NOT_FOUND":
            return "Unable to find a valid discount matching the code entered";
        case "ALREADY_EXISTS":
            return "Discount code has already been applied to the cart";
        case "EXPIRED":
            return "Discount code has expired";
        case "HIGHER_VALUE_DISCOUNT_APPLIED":
            return "Discount couldn't be used with your existing discounts.";
        default:
            return "Unable to find a valid discount matching the code entered";
    }
};

export const CartContext = createContext(null);

const useCart = () => {
    const [cart, setCart] = useState(initialState);
    const [appliedDiscount, setAppliedDiscount] = useState(null);

    const editCartItem = (item) => {
        const productHandleAttr = item.node.attributes.find(attr => attr.key === "_handle")?.value;
        const productPath = productHandleAttr ? `/products/${productHandleAttr}` : `/products/${item.node.merchandise.product.handle}`;

        let measurements = item.node.attributes.reduce((acc, item) => {
            if (item.key.startsWith("C")) {
                acc[item.key] = item.value;
            }
            return acc;
        }, {});

        let customAttributes = item.node.attributes.reduce((acc, item) => {
            let value = item.value;

            if (item.key === 'c_Print Side' || item.key === 'customization_variant_ids') {
                value = item.value.split(",");
            }

            acc[item.key] = value;

            return acc;
        }, {});


        const editData = {
            customAttributes: customAttributes,
            configuration: {
                product: item.node.merchandise.product.id.split('/').pop(),
                variant: item.node.merchandise.id.split('/').pop(),
                quantity: item.node.quantity,
                customization_variant_ids: item.node.attributes
                    .find(attr => attr.key === "customization_variant_ids")?.value.split(","),

                ...measurements
            },
            isEditing: true,
            item,
        }

        navigate(productPath, { state: { editData } });
    };

    const removeCartItem = async (itemId) => {
        let checkoutId = "";
        let updatedCheckout;
        try {
            checkoutId = await getOrCreateCheckoutId();
            updatedCheckout = await updateLineItem(checkoutId, [{ id: itemId, quantity: 0 }]);

            const { cart } = extractCartFromMutation(updatedCheckout, "cartLinesUpdate");
            await updateCart(cart);
        } catch (error) {
            console.error("Error removing cart item:", error);
            sendLog(`[Remove Cart Item] Error`, NEW_RELIC_LEVELS.ERROR, {
                checkoutId,
                itemId,
                graphqlErrors: updatedCheckout?.errors,
            }, error);
            setCart((prevState) => ({ ...prevState, error }));
        }
    };

    // Should be used on any function that changes the Checkout Object
    const updateCart = async (checkout) => {
        if (!Object.keys(checkout || {}).length) return;
        const lineItems = checkout?.lines;

        // Cart State
        const createdAt = new Date(checkout?.createdAt);
        const updatedAt = new Date(checkout?.updatedAt);
        const items = lineItems?.edges?.length ? lineItems.edges : [];


        const calculatedPrice = items.reduce((acc, item) => {
            const itemTotalPrice = item?.node?.attributes.length > 0 && item?.node?.attributes.find((attr) => attr.key === "__basePrice") ? +item.node.attributes.filter((attr) => attr.key === "__basePrice")[0].value * item.node.quantity : +item.node.merchandise.price.amount * item.node.quantity;
            return acc + itemTotalPrice;
        }, 0);


        const newCart = await getStableCheckout({ items });

        let appliedDiscount = await applyProxyDiscountV2({ ...newCart, discountCode: checkout.discountCodes.length > 0 ? checkout.discountCodes[0].code : "" });
        setAppliedDiscount(appliedDiscount);

        if (!appliedDiscount || appliedDiscount?.error === true) {
            appliedDiscount = { total_discounts: 0 };
        }

        const subtotalPrice = { currencyCode: checkout?.cost.subtotalAmount.currencyCode, amount: calculatedPrice };
        const hasDiscount = Boolean(checkout.discountCodes.length);
        const taxPrice = { currencyCode: checkout?.cost.totalAmount.currencyCode, amount: +(appliedDiscount?.tax || 0) };
        const checkoutUrl = checkout?.checkoutUrl;
        const totalPrice = { currencyCode: checkout?.cost.totalAmount.currencyCode, amount: (appliedDiscount?.total_discounts !== 0 ? calculatedPrice - +appliedDiscount.total_discounts : calculatedPrice) };
        const discountCode = checkout?.discountCodes.length > 0 ? checkout.discountCodes[0].code : "";
        const discountPrice = { currencyCode: subtotalPrice.currencyCode, amount: appliedDiscount?.total_discounts !== 0 ? +appliedDiscount.total_discounts : subtotalPrice };

        // console.log("track cart", {
        //     items,
        //     checkoutUrl,
        //     discountCode,
        //     totalPrice,
        //     subtotalPrice,
        //     discountPrice,
        //     createdAt,
        //     updatedAt,
        //     hasDiscount,
        //     error: false,
        //     isLoading: false,
        // });

        setCart(() => ({
            items,
            checkoutUrl,
            discountCode,
            totalPrice,
            subtotalPrice,
            taxPrice,
            discountPrice,
            createdAt,
            updatedAt,
            hasDiscount,
            error: false,
            isLoading: false,
        }));
    };

    // Is called only when initially landing on the page
    const fetchCart = async () => {
        let checkoutId = "";
        let lastCheckoutResponse;
        try {

            // The Shopify "thank you" page sets this cookie after an order is
            // completed. When present, the stored cart is done, so drop it and
            // force a brand new cart (checking cart === null isn't reliable here
            // since a completed cart can still be returned by the Storefront API).
            if (getCookie(CART_COMPLETED_COOKIE) === "true") {
                localStorage.removeItem(checkoutKey);
                deleteCookie(CART_COMPLETED_COOKIE);
            }

            checkoutId = await getOrCreateCheckoutId();
            let checkout = await getCheckout(checkoutId);
            lastCheckoutResponse = checkout;

            // Create new checkout if the old checkout is completed or doesn't exist anymore
            if (checkout?.data?.cart === null) { // || checkout?.data?.cart?.completedAt !== null // ToDo: Check how we can know if the checkout is completed
                checkoutId = await getOrCreateCheckoutId(false, []);
                checkout = await getCheckout(checkoutId);
                lastCheckoutResponse = checkout;
            }

            if (!checkout?.data?.cart?.updatedAt) {
                const error = new Error("Storefront getCheckout did not return a valid cart");
                error.name = "FetchCartInvalidResponse";
                error.response = {
                    data: {
                        graphqlErrors: checkout?.errors,
                        cart: checkout?.data?.cart ?? null,
                    },
                };
                throw error;
            }

            const lastUpdatedAt = new Date(checkout.data.cart.updatedAt).getTime();

            // Should only be called if the cart has been updated or the first time landing in the website
            if (!cart.updatedAt || !(cart.updatedAt.getTime() === lastUpdatedAt)) await updateCart(checkout.data.cart);

        } catch (err) {
            sendLog(`[Fetch Cart] Error`, NEW_RELIC_LEVELS.ERROR, {
                checkoutId,
                graphqlErrors: lastCheckoutResponse?.errors,
                hadCart: Boolean(lastCheckoutResponse?.data?.cart),
            }, err);
            setCart((prevState) => ({ ...prevState, error: err }));
        }
    };

    const addCartItem = async (items) => {
        let checkoutId = "";
        let lineItems = [];
        let updatedCheckout;

        try {
            checkoutId = await getOrCreateCheckoutId();
            lineItems = items.reduce((arr, item) => [...arr, {
                merchandiseId: item.variantId,
                quantity: item?.quantity,
                attributes: item?.customAttributes || [],
            }], []);
            updatedCheckout = await addLineItem(checkoutId, lineItems);

            const { cart, userErrors } = extractCartFromMutation(updatedCheckout, "cartLinesAdd");

            if (userErrors.length > 0) {
                const errorMessage = userErrors[0]?.message || "Unknown error";
                sendLog(`[Add To Cart] Error`, NEW_RELIC_LEVELS.FATAL, {
                    error: errorMessage,
                    userErrors,
                    checkoutId,
                    lineItems,
                    response: updatedCheckout?.data,
                });
            }

            await updateCart(cart, "some");
        } catch (err) {
            sendLog(`[Add To Cart 2] Error`, NEW_RELIC_LEVELS.FATAL, {
                checkoutId,
                lineItems,
                graphqlErrors: updatedCheckout?.errors,
            }, err);

            setCart((prevState) => ({ ...prevState, error: err }));
        }
    };

    const updateCartItem = async (variantId, quantity) => {
        let checkoutId = "";
        let updatedCheckout;
        try {
            checkoutId = await getOrCreateCheckoutId();
            const lineItem = [{
                // eslint-disable-next-line id-length
                id: variantId,
                quantity,
            }];

            updatedCheckout = await updateLineItem(checkoutId, lineItem);

            const { cart } = extractCartFromMutation(updatedCheckout, "cartLinesUpdate");
            await updateCart(cart);
        } catch (err) {
            sendLog(`[Update Cart Item] Error`, NEW_RELIC_LEVELS.ERROR, {
                checkoutId,
                variantId,
                quantity,
                graphqlErrors: updatedCheckout?.errors,
            }, err);
            setCart((prevState) => ({ ...prevState, error: err }));
        }
    };

    const applyDiscount = async (discountCode, onSuccessfullyAppliedCode) => {
        let checkoutId = "";
        let updatedCheckout;
        try {
            checkoutId = await getOrCreateCheckoutId();
            updatedCheckout = await applyDiscountCode(checkoutId, discountCode);

            const { cart, userErrors } = extractCartFromMutation(updatedCheckout, "cartDiscountCodesUpdate");
            const discountApplied = Boolean(cart?.discountCodes?.length);

            if (!discountApplied || userErrors.length > 0) {
                const errorCode = userErrors[0]?.code || "UNKNOWN";
                sendLog(`[Apply Discount] Error`, NEW_RELIC_LEVELS.ERROR, {
                    error: errorCode,
                    userErrors,
                    checkoutId,
                    discountCode,
                });
                setCart((prev) => ({ ...prev, hasDiscount: getAppliedDiscountCodeErrors(userErrors[0]?.code) }));
            } else {
                await updateCart(cart);

                if (onSuccessfullyAppliedCode) onSuccessfullyAppliedCode(updatedCheckout.data.cartDiscountCodesUpdate);
            }
        } catch (err) {
            sendLog(`[Apply Discount 2] Error`, NEW_RELIC_LEVELS.ERROR, {
                checkoutId,
                discountCode,
                graphqlErrors: updatedCheckout?.errors,
            }, err);
            setCart((prevState) => ({ ...prevState, error: err }));
            console.log(err);
        }
    };

    const removeDiscount = async () => {
        let checkoutId = "";
        let updatedCheckout;
        try {
            checkoutId = await getOrCreateCheckoutId();
            updatedCheckout = await applyDiscountCode(checkoutId, "");
            const { cart } = extractCartFromMutation(updatedCheckout, "cartDiscountCodesUpdate");
            await updateCart(cart);
        } catch (err) {
            sendLog(`[Remove Discount] Error`, NEW_RELIC_LEVELS.ERROR, {
                checkoutId,
                graphqlErrors: updatedCheckout?.errors,
            }, err);
            setCart((prevState) => ({ ...prevState, error: err }));
        }
    };

    const checkoutRedirect = async (inputRefs) => {
        const hasPatioProducts = cart.items.some((item) => item.node.attributes.some((attr) => attr.key === "_customProduct"));
        const hasWarrantyProducts = checkWarrantyProducts(cart.items);

        if (hasPatioProducts || hasWarrantyProducts) {
            try {
                const checkoutObject = await getStableCheckout(cart, inputRefs);
                const proxyCheckout = await axios.post(checkoutProxyURL, checkoutObject);
                return proxyCheckout.data;
            } catch (error) {
                sendLog(`[Checkout Redirect] Error`, NEW_RELIC_LEVELS.FATAL, {
                    checkoutUrl: cart.checkoutUrl,
                    hasPatioProducts,
                    hasWarrantyProducts,
                }, error);
                // eslint-disable-next-line no-console
                console.log(error);
                return undefined;
            }
        }


        try {
            const url = new URL(cart.checkoutUrl);
            url.hostname = new URL(process.env.GATSBY_STORE_URL).hostname;

            return { checkout_link: url.toString() };
        } catch (error) {
            sendLog(`[Checkout Redirect] Error`, NEW_RELIC_LEVELS.FATAL, {
                checkoutUrl: cart.checkoutUrl,
            }, error);
            throw error;
        }
    };

    return {
        cart, updateCartItem, addCartItem, fetchCart, applyDiscount, removeDiscount, checkoutRedirect, editCartItem, removeCartItem, appliedDiscount
    };
};

const CartProvider = ({ children }) => {
    const cart = useCart();
    return (
        <CartContext.Provider value={cart}>
            {children}
        </CartContext.Provider>
    );
};

export default CartProvider;
